import type { TableSnapshot } from '@tctp/protocol';
import { useEffect, useRef, useState } from 'react';
import { type OpeningCheck, checkOpening } from './verify';

export type VerificationStatus = 'idle' | 'pending' | 'verified' | 'mismatch';

export interface Verification { status: VerificationStatus; checks: OpeningCheck[] }

const key = (handId: string, position: number, card: number, saltHex: string): string => `${handId}:${position}:${card}:${saltHex}`;

/**
 * Verifies every opening in the current hand against the published commitment, once per opening.
 * The cache is keyed by hand, position, and salt, so re-renders never re-hash and a new hand starts clean.
 */
export function useVerification(snapshot: TableSnapshot | null, subtle?: SubtleCrypto): Verification {
  const cache = useRef(new Map<string, OpeningCheck>());
  const [version, setVersion] = useState(0);
  const hand = snapshot?.hand ?? null;
  const handId = hand?.handId ?? null;
  const openings = hand?.openings ?? [];
  const signature = handId === null ? '' : openings.map(o => key(handId, o.position, o.card, o.saltHex)).join(',');

  useEffect(() => {
    if (!hand || handId === null) return;
    for (const k of [...cache.current.keys()]) if (!k.startsWith(`${handId}:`)) cache.current.delete(k);
    let cancelled = false;
    const missing = openings.filter(o => !cache.current.has(key(handId, o.position, o.card, o.saltHex)));
    if (missing.length === 0) return;
    void Promise.all(missing.map(async o => {
      const check = await checkOpening(handId, hand.commitment, o, subtle);
      cache.current.set(key(handId, o.position, o.card, o.saltHex), check);
    })).then(() => { if (!cancelled) setVersion(v => v + 1); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, subtle]);

  void version;
  if (!hand || handId === null) return { status: 'idle', checks: [] };
  const checks: OpeningCheck[] = [];
  let pending = false;
  for (const o of openings) {
    const c = cache.current.get(key(handId, o.position, o.card, o.saltHex));
    if (c) checks.push(c);
    else pending = true;
  }
  const status: VerificationStatus = pending || checks.length === 0 ? 'pending' : checks.some(c => !c.ok) ? 'mismatch' : 'verified';
  return { status, checks };
}
